
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Loader2, 
  FileText, 
  Shield, 
  AlertCircle,
  Download
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from "sonner";
import { generateGrokResponse } from '@/utils/grokApiClient';

// Add interface for IE specific navigator methods
interface IENavigator extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
}

const AUDIT_TYPES = [
  { id: 'dod', name: 'DoD (Department of Defense)', 
    description: 'Defense Information Systems Agency Security Requirements Guide' },
  { id: 'gao', name: 'GAO (General Audit)',
    description: 'General network security compliance assessment' },
  { id: 'fisma', name: 'FISMA (Federal Information Security Management Act)',
    description: 'Security standards for federal information systems' },
  { id: 'hipaa', name: 'HIPAA (Health Insurance Portability and Accountability Act)',
    description: 'Security and privacy standards for health information' },
  { id: 'soc2', name: 'SOC 2 (Service Organization Control)',
    description: 'Trust services criteria for security, availability, and confidentiality' },
];

// Comprehensive sample data for realistic demo reports
const SAMPLE_AUDIT_DATA = {
  dod: {
    findings: [
      { severity: "CAT I", id: "V-230221", title: "Network devices must enforce approved authorizations", status: "Open" },
      { severity: "CAT II", id: "V-230222", title: "STIG-compliant password policies must be implemented", status: "Open" },
      { severity: "CAT II", id: "V-230223", title: "Administrative accounts must use multi-factor authentication", status: "Compliant" },
      { severity: "CAT III", id: "V-230224", title: "Audit logs must be configured for all security events", status: "Not Reviewed" },
      { severity: "CAT II", id: "V-230225", title: "Network traffic must be encrypted using approved algorithms", status: "Compliant" }
    ],
    controls: [
      { family: "AC", id: "AC-2", title: "Account Management", implementation: "Implemented", effectiveness: "Effective" },
      { family: "AC", id: "AC-3", title: "Access Enforcement", implementation: "Implemented", effectiveness: "Needs Improvement" },
      { family: "IA", id: "IA-2", title: "Identification and Authentication", implementation: "Partially Implemented", effectiveness: "Needs Improvement" },
      { family: "AU", id: "AU-2", title: "Audit Events", implementation: "Implemented", effectiveness: "Effective" },
      { family: "SC", id: "SC-8", title: "Transmission Confidentiality", implementation: "Implemented", effectiveness: "Effective" }
    ]
  },
  gao: {
    controlAreas: [
      { area: "Security Management", rating: "Effective", findings: 2 },
      { area: "Access Controls", rating: "Moderate", findings: 4 },
      { area: "Configuration Management", rating: "Effective", findings: 1 },
      { area: "Segregation of Duties", rating: "Effective", findings: 0 },
      { area: "Contingency Planning", rating: "Needs Improvement", findings: 3 }
    ],
    findings: [
      "Security policies comprehensively address FISCAM requirements with minor gaps in incident response procedures",
      "Access control matrices document role-based permissions but need quarterly review cycles",
      "Intrusion detection systems provide 92% network coverage; perimeter segments require additional sensors",
      "User access reviews conducted semi-annually; recommend quarterly reviews for privileged accounts"
    ]
  },
  fisma: {
    controls: [
      { id: "AC-1", family: "Access Control", title: "Policy and Procedures", status: "Satisfied", risk: "Low" },
      { id: "AC-2", family: "Access Control", title: "Account Management", status: "Satisfied", risk: "Low" },
      { id: "AC-3", family: "Access Control", title: "Access Enforcement", status: "Other than Satisfied", risk: "Moderate" },
      { id: "AU-2", family: "Audit and Accountability", title: "Audit Events", status: "Satisfied", risk: "Low" },
      { id: "AU-6", family: "Audit and Accountability", title: "Audit Review, Analysis, and Reporting", status: "Other than Satisfied", risk: "Moderate" },
      { id: "CA-2", family: "Assessment, Authorization", title: "Control Assessments", status: "Satisfied", risk: "Low" },
      { id: "CM-2", family: "Configuration Management", title: "Baseline Configuration", status: "Satisfied", risk: "Low" },
      { id: "IA-2", family: "Identification and Authentication", title: "User Identification", status: "Other than Satisfied", risk: "Moderate" },
      { id: "SC-7", family: "System and Communications Protection", title: "Boundary Protection", status: "Satisfied", risk: "Low" },
      { id: "SI-2", family: "System and Information Integrity", title: "Flaw Remediation", status: "Satisfied", risk: "Low" }
    ],
    poams: [
      { id: "POAM-001", weakness: "Multi-factor authentication not enforced for all users", scheduledCompletion: "90 days" },
      { id: "POAM-002", weakness: "Audit log review procedures need formalization", scheduledCompletion: "60 days" }
    ]
  },
  hipaa: {
    safeguards: [
      { type: "Administrative", requirement: "§164.308(a)(1)(i)", title: "Security Management Process", status: "Compliant", notes: "Risk analysis conducted annually" },
      { type: "Administrative", requirement: "§164.308(a)(3)(i)", title: "Workforce Security", status: "Compliant", notes: "Authorization procedures in place" },
      { type: "Administrative", requirement: "§164.308(a)(5)(i)", title: "Security Awareness Training", status: "Partially Compliant", notes: "Training needs more frequent updates" },
      { type: "Physical", requirement: "§164.310(a)(1)", title: "Facility Access Controls", status: "Compliant", notes: "Access controls and monitoring active" },
      { type: "Physical", requirement: "§164.310(d)(1)", title: "Device and Media Controls", status: "Compliant", notes: "Disposal procedures documented" },
      { type: "Technical", requirement: "§164.312(a)(1)", title: "Access Control", status: "Compliant", notes: "Unique user IDs and emergency access procedures" },
      { type: "Technical", requirement: "§164.312(b)", title: "Audit Controls", status: "Partially Compliant", notes: "Logs maintained but review frequency insufficient" },
      { type: "Technical", requirement: "§164.312(e)(1)", title: "Transmission Security", status: "Compliant", notes: "Encryption for all PHI transmissions" }
    ],
    findings: [
      "PHI data flow mapping complete; encryption properly implemented for data at rest and in transit",
      "Access control mechanisms enforce minimum necessary standard with role-based access",
      "Audit logs captured for all ePHI access but review procedures need enhancement",
      "Business Associate Agreements in place for all third-party service providers"
    ]
  },
  soc2: {
    criteria: [
      { id: "CC1.1", category: "Common Criteria", title: "Control Environment", assessment: "No Exceptions", notes: "Strong governance structure" },
      { id: "CC2.1", category: "Common Criteria", title: "Communication and Information", assessment: "No Exceptions", notes: "Effective communication channels" },
      { id: "CC3.1", category: "Common Criteria", title: "Risk Assessment", assessment: "Exception Noted", notes: "Risk assessments need quarterly frequency" },
      { id: "CC6.1", category: "Common Criteria", title: "Logical and Physical Access", assessment: "No Exceptions", notes: "Comprehensive access controls" },
      { id: "CC7.2", category: "Common Criteria", title: "System Monitoring", assessment: "Exception Noted", notes: "Gaps in monitoring coverage identified" },
      { id: "A1.2", category: "Availability", title: "Environmental Protections", assessment: "No Exceptions", notes: "Redundant infrastructure in place" },
      { id: "C1.1", category: "Confidentiality", title: "Confidential Information", assessment: "No Exceptions", notes: "Data classification implemented" }
    ],
    findings: [
      "Control environment demonstrates maturity with documented policies and procedures",
      "Security monitoring architecture covers critical systems; recommend expansion to development environments",
      "Third-party vendor management program active; annual assessments should include SOC 2 review",
      "Incident response procedures well-documented with regular testing conducted"
    ]
  }
};

interface AuditStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'inProgress' | 'completed';
  progress: number;
}

interface SecurityAuditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  overallProgress: number;
  onAuditTypeChange: (type: string) => void;
  selectedAuditType: string;
  steps: AuditStep[];
  onDownloadReport: () => void;
  onStartAudit: () => void;
  auditInProgress: boolean;
  auditCompleted?: boolean;
}

interface ReportData {
  findings: string[];
  score: number;
  date: string;
  recommendations: string[];
}

const SecurityAuditPopup: React.FC<SecurityAuditPopupProps> = ({
  isOpen,
  onClose,
  overallProgress,
  onAuditTypeChange,
  selectedAuditType,
  steps,
  onDownloadReport,
  onStartAudit,
  auditInProgress,
  auditCompleted = false
}) => {
  const [reportPreviewVisible, setReportPreviewVisible] = useState(false);
  const [reportData, setReportData] = useState<ReportData>({
    findings: [],
    score: 0,
    date: new Date().toLocaleDateString(),
    recommendations: []
  });
  
  const [aiGeneratedContent, setAiGeneratedContent] = useState<string | null>(null);
  const [isGeneratingAiContent, setIsGeneratingAiContent] = useState(false);
  
  // Update report data when audit completes or audit type changes
  useEffect(() => {
    if (auditCompleted || overallProgress >= 100) {
      generateReportData();
    }
  }, [auditCompleted, selectedAuditType, overallProgress]);
  
  // Generate sample report data based on the selected audit type
  const generateReportData = () => {
    const score = Math.floor(Math.random() * 15) + 80; // Random score between 80-95
    const date = new Date().toLocaleDateString();
    
    // Extract findings based on audit type
    let findings: string[] = [];
    const auditData = SAMPLE_AUDIT_DATA[selectedAuditType as keyof typeof SAMPLE_AUDIT_DATA];
    
    if ('findings' in auditData && Array.isArray(auditData.findings)) {
      if (typeof auditData.findings[0] === 'string') {
        findings = auditData.findings as string[];
      } else {
        // For complex objects, extract summary
        findings = (auditData.findings as any[]).slice(0, 4).map((f: any) => 
          f.title || f.requirement || f.area || 'Assessment finding'
        );
      }
    }
    
    // Generate recommendations based on audit type
    const recommendations = [
      "Implement recommended security controls within 90 days",
      "Enhance monitoring and logging procedures",
      "Conduct quarterly security reviews and audits",
      "Update security policies and documentation",
      "Provide additional staff training on security procedures"
    ];
    
    setReportData({
      findings,
      score,
      date,
      recommendations
    });
    
    // Auto-show report preview when audit is completed
    if (auditCompleted && !reportPreviewVisible && overallProgress >= 100) {
      setReportPreviewVisible(true);
    }
    
    // Generate AI content when report is ready
    if ((auditCompleted || overallProgress >= 100) && !isGeneratingAiContent && !aiGeneratedContent) {
      generateAIReportContent();
    }
  };
  
  // Generate AI-powered report content
  const generateAIReportContent = async () => {
    setIsGeneratingAiContent(true);
    
    const auditType = AUDIT_TYPES.find(type => type.id === selectedAuditType) || AUDIT_TYPES[1];
    
    try {
      // Generate AI content using GROK
      const prompt = `Generate a detailed security audit report for a ${auditType.name} compliance assessment. 
      Include specific findings related to network security, access controls, encryption standards, and policy compliance. 
      The overall compliance score is ${reportData.score}%. 
      Format the report in a professional structure with sections for:
      - Executive Summary
      - Key Findings
      - Detailed Analysis
      - Specific Recommendations
      - Conclusion`;
      
      const content = await generateGrokResponse(prompt);
      setAiGeneratedContent(content);
    } catch (error) {
      console.error("Error generating AI report content:", error);
      // Fallback to template-based report if AI generation fails
      toast.error("AI report generation failed, using template report instead", {
        description: "Unable to generate AI-powered report content"
      });
    } finally {
      setIsGeneratingAiContent(false);
    }
  };
  
  // Generate a preview of the report based on the selected audit type
  const getReportPreviewContent = () => {
    const auditType = AUDIT_TYPES.find(type => type.id === selectedAuditType) || AUDIT_TYPES[1]; // Default to GAO
    const completedSteps = steps.filter(step => step.status === 'completed').length;
    const totalSteps = steps.length;
    
    return (
      <div className="mt-4 p-4 bg-muted/30 rounded-md border text-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <FileText className="h-4 w-4 mr-2 text-primary" />
            <span className="font-medium">{auditType.name} Report Preview</span>
          </div>
          <span className="text-xs text-muted-foreground">{reportData.date}</span>
        </div>
        
        <div className="space-y-2">
          <p><span className="font-medium">Audit Status:</span> {auditCompleted || overallProgress >= 100 ? 'Complete' : 'In Progress'}</p>
          <p><span className="font-medium">Steps Completed:</span> {completedSteps} of {totalSteps}</p>
          <p><span className="font-medium">Compliance Score:</span> {auditCompleted || overallProgress >= 100 ? `${reportData.score}%` : 'Calculating...'}</p>
          
          {(auditCompleted || overallProgress >= 100) && (
            <>
              <div>
                <p className="font-medium mb-1">Key Findings:</p>
                <ul className="pl-5 list-disc space-y-1">
                  {reportData.findings.slice(0, 2).map((finding, index) => (
                    <li key={index} className="text-xs">{finding}</li>
                  ))}
                  {reportData.findings.length > 2 && (
                    <li className="text-xs text-muted-foreground italic">
                      ...and {reportData.findings.length - 2} more findings (see full report)
                    </li>
                  )}
                </ul>
              </div>
              
              <div>
                <p className="font-medium mb-1">Top Recommendations:</p>
                <ul className="pl-5 list-disc space-y-1">
                  {reportData.recommendations.slice(0, 2).map((rec, index) => (
                    <li key={index} className="text-xs">{rec}</li>
                  ))}
                  {reportData.recommendations.length > 2 && (
                    <li className="text-xs text-muted-foreground italic">
                      ...and {reportData.recommendations.length - 2} more recommendations
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
          
          {isGeneratingAiContent && (
            <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded flex items-center">
              <Loader2 className="h-4 w-4 mr-2 text-blue-500 animate-spin" />
              <span className="text-xs">Enhanced AI report generation in progress...</span>
            </div>
          )}
          
          {overallProgress > 25 && overallProgress < 100 && !auditCompleted && (
            <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded flex items-center">
              <Loader2 className="h-4 w-4 mr-2 text-blue-500 animate-spin" />
              <span className="text-xs">Report generation in progress... ({overallProgress}%)</span>
            </div>
          )}
        </div>
        
        {(auditCompleted || overallProgress >= 100) && (
          <p className="mt-2 text-xs text-muted-foreground italic">Download the full report for complete findings and recommendations.</p>
        )}
      </div>
    );
  };
  
  // Download function for Word document (.docx)
  const handleDownloadReport = () => {
    const auditType = AUDIT_TYPES.find(type => type.id === selectedAuditType) || AUDIT_TYPES[1]; // Default to GAO
    const fileName = `${auditType.name.replace(/[()]/g, '').replace(/\s+/g, '_')}_Security_Audit_Report_${new Date().toISOString().split('T')[0]}.docx`;
    
    try {
      // Create report content
      let reportContent;
      
      // Use AI generated content if available, otherwise fall back to the template
      if (aiGeneratedContent) {
        reportContent = formatReportDocument(auditType.name, reportData, aiGeneratedContent);
      } else {
        reportContent = generateReportContent(auditType.name, reportData);
      }
      
      // Convert text content to a Word document format (HTML that MS Word can open)
      const wordContent = convertToWordDocument(reportContent);
      
      // Create a Blob with MS Word mimetype
      const blob = new Blob([wordContent], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      
      // Use the download attribute for direct download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      // Show success notification
      toast.success(`Report "${fileName}" downloading`, {
        description: "Your Word document is being downloaded to your computer."
      });
      
      // Call the original onDownloadReport handler for any additional logic
      onDownloadReport();
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download Word report", {
        description: "There was an error generating your report. Please try again."
      });
      
      // Try alternative download approach
      try {
        downloadWordDocumentAlternative(fileName);
      } catch (fallbackError) {
        console.error("Alternative download approach failed:", fallbackError);
      }
    }
  };
  
  // Function to convert text content to a Word-compatible HTML document
  const convertToWordDocument = (content: string) => {
    // Format the content as Word-compatible HTML
    const wordHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <meta name="ProgId" content="Word.Document">
        <meta name="Generator" content="Microsoft Word 15">
        <meta name="Originator" content="Microsoft Word 15">
        <title>Security Audit Report</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          body {
            font-family: 'Calibri', sans-serif;
            font-size: 11pt;
            line-height: 1.5;
          }
          h1, h2, h3, h4 {
            font-family: 'Calibri', sans-serif;
            font-weight: bold;
            margin-top: 12pt;
            margin-bottom: 6pt;
          }
          h1 { font-size: 16pt; }
          h2 { 
            font-size: 14pt; 
            border-bottom: 1pt solid #CCCCCC;
            padding-bottom: 4pt;
          }
          h3 { font-size: 12pt; }
          hr { border: 1pt solid #EEEEEE; }
          p { margin: 6pt 0; }
        </style>
      </head>
      <body>
        ${content.replace(/\n\n/g, '<p></p>')
                .replace(/\n/g, '<br>')
                .replace(/----------------------------------------------------------------------/g, '<hr/>')}
      </body>
      </html>`;
      
    return wordHtml;
  };
  
  // Alternative download approach as fallback, fixed for TypeScript
  const downloadWordDocumentAlternative = (fileName: string) => {
    const auditType = AUDIT_TYPES.find(type => type.id === selectedAuditType) || AUDIT_TYPES[1];
    
    // Create content
    let content;
    if (aiGeneratedContent) {
      content = formatReportDocument(auditType.name, reportData, aiGeneratedContent);
    } else {
      content = generateReportContent(auditType.name, reportData);
    }
    
    const wordContent = convertToWordDocument(content);
    
    // Create a blob with Word document mime type
    const blob = new Blob([wordContent], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    
    // Try with msSaveBlob which works better in IE/Edge (with proper TypeScript check)
    const ieNavigator = window.navigator as IENavigator;
    if (ieNavigator && ieNavigator.msSaveOrOpenBlob) {
      ieNavigator.msSaveOrOpenBlob(blob, fileName);
      return;
    }
    
    // Try iframe approach
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    try {
      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(wordContent);
        doc.close();
        
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            toast.success("Report opened for printing", {
              description: "Please save the document using the Print dialog."
            });
          } catch (e) {
            console.error("Print error:", e);
            toast.error("Unable to open print dialog", {
              description: "Try right-clicking and saving the report manually."
            });
          }
        }, 1000);
      }
    } catch (e) {
      console.error("Iframe approach error:", e);
      document.body.removeChild(iframe);
      throw e;
    }
  };
  
  // Format AI content into a proper document structure
  const formatReportDocument = (auditTypeName: string, data: ReportData, aiContent: string) => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    return `
SECURITY AUDIT REPORT
=====================

REPORT DATE: ${currentDate}
AUDIT TYPE: ${auditTypeName}
COMPLIANCE SCORE: ${data.score}%
GENERATED BY: S2AI SECURITY AUTOMATION SYSTEM WITH GROK AI
CLASSIFICATION: CONFIDENTIAL

----------------------------------------------------------------------

${aiContent}

----------------------------------------------------------------------

METHODOLOGY
----------

The following steps were completed during this assessment:
${steps.map((step) => `- ${step.title}: ${step.description}`).join('\n')}

----------------------------------------------------------------------

This report was generated by the S2AI Security Automation System with GROK AI
© ${new Date().getFullYear()} S2AI Secure Networks
Document ID: SEC-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}-${new Date().toISOString().split('T')[0]}
`;
  };
  
  // Generate the appropriate report content based on audit type
  const generateReportContent = (auditTypeName: string, data: ReportData) => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const auditData = SAMPLE_AUDIT_DATA[selectedAuditType as keyof typeof SAMPLE_AUDIT_DATA];
    
    // Create a comprehensive report specific to the audit type
    let specificContent = '';
    
    switch (selectedAuditType) {
      case 'dod':
        const dodData = auditData as typeof SAMPLE_AUDIT_DATA.dod;
        const catICount = dodData.findings.filter(f => f.severity === "CAT I").length;
        const catIICount = dodData.findings.filter(f => f.severity === "CAT II").length;
        const catIIICount = dodData.findings.filter(f => f.severity === "CAT III").length;
        
        specificContent = `
═══════════════════════════════════════════════════════════════════════
DEPARTMENT OF DEFENSE (DoD)
SECURITY TECHNICAL IMPLEMENTATION GUIDE (STIG) COMPLIANCE ASSESSMENT
═══════════════════════════════════════════════════════════════════════

ASSESSMENT AUTHORITY: Defense Information Systems Agency (DISA)
ASSESSMENT STANDARD: DISA STIG Version 2, Release 4
INFORMATION SYSTEM: S2AI Network Infrastructure
SECURITY CATEGORIZATION: HIGH (per FIPS 199)
AUTHORITY TO OPERATE STATUS: PROVISIONAL - CONDITIONS APPLY
ASSESSMENT PERIOD: ${currentDate}
CLASSIFICATION: CONTROLLED UNCLASSIFIED INFORMATION (CUI)

----------------------------------------------------------------------
1. EXECUTIVE SUMMARY
----------------------------------------------------------------------

This Security Technical Implementation Guide (STIG) compliance assessment 
was conducted in accordance with DoD Instruction 8500.01 (Cybersecurity) 
and CNSSI 1253 (Security Categorization and Control Selection). The 
assessment evaluated ${dodData.findings.length} STIG controls across the network infrastructure.

OVERALL STIG COMPLIANCE: ${data.score}%

VULNERABILITY SEVERITY DISTRIBUTION:
  • Category I (CAT I) - High:        ${catICount} finding(s)
  • Category II (CAT II) - Medium:    ${catIICount} finding(s)
  • Category III (CAT III) - Low:     ${catIIICount} finding(s)

Category I findings represent immediate threats to confidentiality, 
integrity, or availability and require immediate remediation within 30 days.

----------------------------------------------------------------------
2. SECURITY POSTURE ASSESSMENT
----------------------------------------------------------------------

DoD COMPLIANCE METRICS:
  ├─ Configuration Management:          ${data.score - 5}%
  ├─ Patch Management:                  ${data.score + 2 > 100 ? 100 : data.score + 2}%
  ├─ Access Control Implementation:     ${data.score - 3}%
  ├─ Encryption Standards (FIPS 140-2): ${data.score + 1 > 100 ? 100 : data.score + 1}%
  ├─ Audit & Accountability:            ${data.score}%
  └─ Incident Response:                 ${data.score - 2}%

SYSTEM IMPACT LEVEL: HIGH
  • Confidentiality: HIGH
  • Integrity: HIGH  
  • Availability: HIGH

----------------------------------------------------------------------
3. STIG VULNERABILITY FINDINGS
----------------------------------------------------------------------

${dodData.findings.map((finding, idx) => `
FINDING ${idx + 1}: ${finding.severity}
  Vulnerability ID: ${finding.id}
  Rule Title: ${finding.title}
  Status: ${finding.status}
  Severity: ${finding.severity}
  
  ${finding.severity === 'CAT I' ? 'IMMEDIATE ACTION REQUIRED: This is a Category I vulnerability that could allow unauthorized access to classified information or cause system compromise.' :
    finding.severity === 'CAT II' ? 'REMEDIATION REQUIRED: This Category II vulnerability could degrade system security posture and should be addressed within 90 days.' :
    'INFORMATIONAL: This Category III finding represents a low-risk condition that should be addressed during routine maintenance.'}
`).join('\n')}

----------------------------------------------------------------------
4. NIST 800-53 CONTROL FAMILY ASSESSMENT
----------------------------------------------------------------------

${dodData.controls.map(control => `
${control.family}-${control.id}: ${control.title}
  Implementation Status: ${control.implementation}
  Control Effectiveness: ${control.effectiveness}
  Assessment Method: Examine, Interview, Test
  ${control.effectiveness !== 'Effective' ? '  ⚠ Requires Attention: Control weaknesses identified' : '  ✓ Operating as intended'}
`).join('\n')}

----------------------------------------------------------------------
5. RISK MANAGEMENT FRAMEWORK (RMF) FINDINGS
----------------------------------------------------------------------

RMF Step 1 - Categorize: COMPLETE
  System categorized as HIGH impact per FIPS 199 and CNSSI 1253

RMF Step 2 - Select: COMPLETE  
  NIST SP 800-53 Rev 5 controls selected for HIGH baseline

RMF Step 3 - Implement: IN PROGRESS
  ${dodData.controls.filter(c => c.implementation === 'Implemented').length}/${dodData.controls.length} controls fully implemented
  ${dodData.controls.filter(c => c.implementation === 'Partially Implemented').length} controls partially implemented

RMF Step 4 - Assess: COMPLETE
  Independent assessment conducted per NIST SP 800-53A

RMF Step 5 - Authorize: PENDING
  POA&M required for authorization decision

RMF Step 6 - Monitor: OPERATIONAL
  Continuous monitoring program active per NIST SP 800-137

----------------------------------------------------------------------
6. MANDATORY REMEDIATION ACTIONS
----------------------------------------------------------------------

IMMEDIATE (0-30 DAYS) - Category I Findings:
${dodData.findings.filter(f => f.severity === 'CAT I' && f.status === 'Open').map((f, idx) => 
  `  ${idx + 1}. ${f.id}: ${f.title}\n     ACTION: Implement compensating controls and submit POA&M`).join('\n')}

SHORT-TERM (31-90 DAYS) - Category II Findings:
${dodData.findings.filter(f => f.severity === 'CAT II' && f.status === 'Open').map((f, idx) => 
  `  ${idx + 1}. ${f.id}: ${f.title}`).join('\n')}

LONG-TERM (91-180 DAYS) - Category III Findings:
${dodData.findings.filter(f => f.severity === 'CAT III').map((f, idx) => 
  `  ${idx + 1}. ${f.id}: ${f.title}`).join('\n')}

----------------------------------------------------------------------
7. AUTHORIZING OFFICIAL RECOMMENDATIONS
----------------------------------------------------------------------

Based on this assessment, the following recommendations are provided:

1. IMMEDIATE ACTIONS:
   • Remediate all Category I vulnerabilities within 30 days
   • Submit Plan of Action & Milestones (POA&M) to Authorizing Official
   • Implement enhanced monitoring for high-risk systems

2. AUTHORIZATION DECISION:
   Recommend PROVISIONAL Authority to Operate (ATO) for 90 days
   contingent upon completion of mandatory remediation actions and
   acceptance of residual risk by the Authorizing Official.

3. CONTINUOUS MONITORING:
   • Weekly vulnerability scanning
   • Monthly STIG compliance verification
   • Quarterly security control assessments
   • Annual authorization review

----------------------------------------------------------------------
8. REFERENCES AND AUTHORITY
----------------------------------------------------------------------

• DoD Instruction 8500.01: Cybersecurity (14 March 2014)
• DoD Instruction 8510.01: Risk Management Framework (12 March 2014)  
• CNSSI 1253: Security Categorization and Control Selection
• NIST Special Publication 800-53 Rev 5: Security Controls
• NIST Special Publication 800-53A Rev 5: Assessing Security Controls
• NIST Special Publication 800-37 Rev 2: Risk Management Framework
• DISA STIG Library: https://public.cyber.mil/stigs/
• FIPS 199: Standards for Security Categorization
• FIPS 140-2: Security Requirements for Cryptographic Modules

----------------------------------------------------------------------
ASSESSMENT TEAM
----------------------------------------------------------------------
Lead Assessor: S2AI Security Automation System
Assessment Method: Automated + Manual Review
Assessment Date: ${currentDate}

DISTRIBUTION: Authorizing Official, Information System Owner, ISSM, ISSO
`;
        break;
        
      case 'fisma':
        const fismaData = auditData as typeof SAMPLE_AUDIT_DATA.fisma;
        const satisfied = fismaData.controls.filter(c => c.status === 'Satisfied').length;
        const other = fismaData.controls.filter(c => c.status === 'Other than Satisfied').length;
        
        specificContent = `
═══════════════════════════════════════════════════════════════════════
FEDERAL INFORMATION SECURITY MODERNIZATION ACT (FISMA)
ANNUAL SECURITY ASSESSMENT REPORT
═══════════════════════════════════════════════════════════════════════

REPORTING AGENCY: S2AI Network Operations  
FISCAL YEAR: ${new Date().getFullYear()}
SYSTEM NAME: S2AI Network Infrastructure
UNIQUE IDENTIFIER: S2AI-NET-001
FIPS 199 CATEGORY: MODERATE
AUTHORIZATION STATUS: AUTHORIZED TO OPERATE (ATO)
AUTHORIZATION DATE: ${currentDate}
ASSESSMENT DATE: ${currentDate}

----------------------------------------------------------------------
1. EXECUTIVE SUMMARY
----------------------------------------------------------------------

This Security Assessment Report (SAR) documents the security control
assessment conducted in accordance with FISMA (44 U.S.C. § 3551 et seq.),
OMB Circular A-130, and NIST Special Publication 800-53 Revision 5.

The information system has been assessed against the NIST SP 800-53 Rev 5
security control baseline for MODERATE impact systems. This assessment
evaluates ${fismaData.controls.length} security controls across 18 control families.

OVERALL FISMA COMPLIANCE: ${data.score}%
AUTHORIZATION STATUS: AUTHORIZED TO OPERATE (ATO)
ATO EXPIRATION DATE: ${new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}

CONTROL ASSESSMENT SUMMARY:
  ├─ Satisfied: ${satisfied} controls (${Math.round(satisfied/fismaData.controls.length * 100)}%)
  └─ Other than Satisfied: ${other} controls (${Math.round(other/fismaData.controls.length * 100)}%)

RISK DETERMINATION: MODERATE RISK - ACCEPTABLE

----------------------------------------------------------------------
2. SYSTEM CATEGORIZATION (PER FIPS 199)
----------------------------------------------------------------------

SECURITY IMPACT LEVELS:
  ├─ Confidentiality: MODERATE
  ├─ Integrity: MODERATE
  └─ Availability: MODERATE

OVERALL SYSTEM CATEGORIZATION: MODERATE

RATIONALE:
The system processes, stores, and transmits information where unauthorized
disclosure, modification, or loss of availability could have a serious
adverse effect on organizational operations, organizational assets, or
individuals, but would not cause catastrophic harm.

----------------------------------------------------------------------
3. NIST 800-53 CONTROL ASSESSMENT RESULTS  
----------------------------------------------------------------------

${fismaData.controls.map(control => `
Control ${control.id}: ${control.title}
  Control Family: ${control.family}
  Implementation Status: ${control.status}
  Risk Level: ${control.risk}
  Assessment Methods: Examine, Interview, Test
  
  ${control.status === 'Satisfied' 
    ? '✓ SATISFIED: Control is implemented correctly, operating as intended, and producing the desired outcome with respect to meeting security requirements.'
    : `⚠ OTHER THAN SATISFIED: Control implementation has weaknesses. See POA&M ${fismaData.poams.findIndex(p => p.weakness.toLowerCase().includes(control.title.toLowerCase().split(' ')[0])) + 1 || 'TBD'} for remediation plan.`}
`).join('\n')}

----------------------------------------------------------------------
4. CONTINUOUS MONITORING PROGRAM
----------------------------------------------------------------------

MONITORING STRATEGY:
The organization has implemented a continuous monitoring program in
accordance with NIST SP 800-137. Key elements include:

ONGOING ASSESSMENTS:
  • Vulnerability Scanning: Weekly automated scans
  • Configuration Management: Daily baseline verification
  • Patch Management: Monthly patch deployment cycle  
  • Security Control Assessment: Annual comprehensive review
  • Penetration Testing: Annual authorized testing

SECURITY STATUS REPORTING:
  • Monthly: Security metrics dashboard to System Owner
  • Quarterly: Security posture briefing to CIO
  • Annually: FISMA metrics to OMB via CyberScope

CONFIGURATION MANAGEMENT:
  • Baseline configurations documented and maintained
  • Change control board reviews all configuration changes
  • Automated configuration monitoring deployed

IMPACT ANALYSIS:
  • Changes assessed for security impact before implementation
  • Risk analysis conducted for all moderate/high impact changes

----------------------------------------------------------------------
5. PLAN OF ACTION & MILESTONES (POA&M)
----------------------------------------------------------------------

OPEN POA&M ITEMS: ${fismaData.poams.length}

${fismaData.poams.map((poam, idx) => `
POA&M ${idx + 1}: ${poam.id}
  Control Weakness: ${poam.weakness}
  Risk Level: Moderate
  Scheduled Completion: ${poam.scheduledCompletion}
  Resources Required: Staff time, potential tool procurement
  Status: In Progress
  Milestone 1: Requirements analysis - Complete
  Milestone 2: Solution design - In Progress (30% complete)
  Milestone 3: Implementation - Scheduled
  Milestone 4: Testing and validation - Scheduled
  Milestone 5: Authorization - Scheduled
`).join('\n')}

----------------------------------------------------------------------
6. INCIDENT RESPONSE CAPABILITY
----------------------------------------------------------------------

INCIDENT RESPONSE PROGRAM STATUS: OPERATIONAL

The organization maintains an incident response capability in accordance
with NIST SP 800-61 Rev 2. Key capabilities include:

  ✓ Incident Response Policy (last updated: ${new Date(new Date().setMonth(new Date().getMonth() - 3)).toLocaleDateString()})
  ✓ Incident Response Plan (last updated: ${new Date(new Date().setMonth(new Date().getMonth() - 6)).toLocaleDateString()})
  ✓ Incident Response Team: 5 trained personnel
  ✓ 24/7 Incident Reporting Hotline
  ✓ Incident Tracking System: ServiceNow  
  ✓ Annual Incident Response Exercise: Last conducted ${new Date(new Date().setMonth(new Date().getMonth() - 2)).toLocaleDateString()}

INCIDENTS THIS REPORTING PERIOD: 0 reportable incidents

----------------------------------------------------------------------
7. CONTINGENCY PLANNING & DISASTER RECOVERY
----------------------------------------------------------------------

BUSINESS CONTINUITY STATUS: TESTED AND OPERATIONAL

  ✓ Contingency Plan approved: ${new Date(new Date().setMonth(new Date().getMonth() - 8)).toLocaleDateString()}
  ✓ Recovery Time Objective (RTO): 4 hours
  ✓ Recovery Point Objective (RPO): 1 hour
  ✓ Backup Frequency: Daily incremental, Weekly full
  ✓ Backup Testing: Monthly restoration tests
  ✓ Annual Contingency Plan Test: Last conducted ${new Date(new Date().setMonth(new Date().getMonth() - 4)).toLocaleDateString()}
  ✓ Alternate Processing Site: Established and tested

----------------------------------------------------------------------
8. PRIVACY IMPACT ASSESSMENT
----------------------------------------------------------------------

PIA STATUS: Current (last review: ${new Date(new Date().setMonth(new Date().getMonth() - 10)).toLocaleDateString()})
PII COLLECTED: Limited (Names, email addresses for authentication)
PRIVACY CONTROLS: Implemented per NIST SP 800-53 Rev 5 Appendix J

----------------------------------------------------------------------
9. SECURITY TRAINING & AWARENESS
----------------------------------------------------------------------

ANNUAL SECURITY AWARENESS TRAINING:
  • Completion Rate: 98%
  • Training Platform: KnowBe4
  • Last Training Cycle: ${new Date(new Date().setMonth(new Date().getMonth() - 2)).toLocaleDateString()}

ROLE-BASED SECURITY TRAINING:
  • System Administrators: Annual specialized training - Current
  • Privileged Users: Quarterly refresher training - Current
  • Developers: Secure coding training - Current

----------------------------------------------------------------------
10. AUTHORIZING OFFICIAL DETERMINATION
----------------------------------------------------------------------

AUTHORIZATION DECISION: AUTHORIZE TO OPERATE (ATO)

DETERMINATION:
Based on the results of this security assessment, the Authorizing Official
has determined that the risk to organizational operations, organizational
assets, and individuals is ACCEPTABLE. The system is granted an Authority
to Operate for THREE (3) YEARS, expiring ${new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}.

CONDITIONS:
1. Complete remediation of POA&M items within scheduled timeframes
2. Maintain continuous monitoring program
3. Report any significant changes to the Authorizing Official
4. Conduct annual security control assessment

RISK ACCEPTANCE:
The Authorizing Official accepts the residual risks associated with the
${other} "Other than Satisfied" security controls, contingent upon execution
of the POA&M remediation plan.

----------------------------------------------------------------------
11. REFERENCES & COMPLIANCE FRAMEWORKS
----------------------------------------------------------------------

• FISMA: 44 U.S.C. § 3551 et seq.
• OMB Circular A-130: Managing Information as a Strategic Resource
• NIST SP 800-53 Rev 5: Security and Privacy Controls
• NIST SP 800-53A Rev 5: Assessing Security and Privacy Controls  
• NIST SP 800-37 Rev 2: Risk Management Framework
• NIST SP 800-60 Vol 1 Rev 1: Guide for Mapping Types of Information
• NIST SP 800-137: Information Security Continuous Monitoring
• FIPS 199: Standards for Security Categorization
• FIPS 200: Minimum Security Requirements

----------------------------------------------------------------------
ASSESSMENT TEAM & APPROVAL
----------------------------------------------------------------------

Independent Assessor: S2AI Security Automation System
Assessment Date: ${currentDate}
Assessment Method: Automated scanning, manual review, documentation examination

System Owner: _________________________ Date: __________
Information System Security Officer: _________________________ Date: __________
Authorizing Official: _________________________ Date: __________

REPORT CLASSIFICATION: CONTROLLED UNCLASSIFIED INFORMATION (CUI)
DISTRIBUTION: System Owner, CISO, Authorizing Official, OMB (via CyberScope)
`;
        break;
        
      case 'hipaa':
        const hipaaData = auditData as typeof SAMPLE_AUDIT_DATA.hipaa;
        const compliant = hipaaData.safeguards.filter(s => s.status === 'Compliant').length;
        const partial = hipaaData.safeguards.filter(s => s.status === 'Partially Compliant').length;
        
        specificContent = `
═══════════════════════════════════════════════════════════════════════
HEALTH INSURANCE PORTABILITY AND ACCOUNTABILITY ACT (HIPAA)
SECURITY RULE COMPLIANCE ASSESSMENT REPORT
═══════════════════════════════════════════════════════════════════════

COVERED ENTITY: S2AI Healthcare Network Infrastructure
ASSESSMENT TYPE: Security Rule Compliance Review
REGULATORY AUTHORITY: 45 CFR Parts 160, 162, and 164
ASSESSMENT DATE: ${currentDate}
ASSESSMENT SCOPE: Administrative, Physical, and Technical Safeguards
COVERED ENTITY TYPE: Healthcare Provider / Business Associate
COMPLIANCE STATUS: SUBSTANTIAL COMPLIANCE - REMEDIATION IN PROGRESS

----------------------------------------------------------------------
1. EXECUTIVE SUMMARY
----------------------------------------------------------------------

This Security Rule assessment evaluates compliance with the Health Insurance
Portability and Accountability Act (HIPAA) Security Rule requirements as
codified in 45 CFR §§ 164.302 through 164.318.

The assessment examined the implementation of Required and Addressable
implementation specifications across the three safeguard categories:
Administrative, Physical, and Technical.

OVERALL HIPAA SECURITY RULE COMPLIANCE: ${data.score}%

SAFEGUARD IMPLEMENTATION STATUS:
  ├─ Compliant: ${compliant} safeguards (${Math.round(compliant/hipaaData.safeguards.length * 100)}%)
  └─ Partially Compliant: ${partial} safeguards (${Math.round(partial/hipaaData.safeguards.length * 100)}%)

COMPLIANCE DETERMINATION:
The covered entity demonstrates substantial compliance with HIPAA Security
Rule requirements. Identified deficiencies are being addressed through a
comprehensive remediation plan with defined timelines.

BREACH NOTIFICATION ASSESSMENT:
No reportable breaches of unsecured PHI in the assessment period.

----------------------------------------------------------------------
2. ADMINISTRATIVE SAFEGUARDS (§164.308)
----------------------------------------------------------------------

${hipaaData.safeguards.filter(s => s.type === 'Administrative').map(safeguard => `
${safeguard.requirement}: ${safeguard.title}
  Requirement Type: ${safeguard.requirement.includes('(a)') ? 'REQUIRED' : 'ADDRESSABLE'}
  Implementation Status: ${safeguard.status}
  
  Assessment Findings: ${safeguard.notes}
  
  ${safeguard.status === 'Compliant' 
    ? '✓ COMPLIANT: Implementation specifications are properly implemented and consistently applied.'
    : '⚠ PARTIALLY COMPLIANT: Implementation has minor gaps that should be addressed. See remediation plan below.'}
`).join('\n')}

ADDITIONAL ADMINISTRATIVE SAFEGUARD FINDINGS:

Security Management Process (§164.308(a)(1)):
  ✓ Risk Analysis: Conducted annually per §164.308(a)(1)(ii)(A)
  ✓ Risk Management: Remediation tracking system in place
  ✓ Sanction Policy: Workforce sanctions policy documented and enforced
  ✓ Information System Activity Review: Quarterly audit log reviews

Assigned Security Responsibility (§164.308(a)(2)):
  ✓ Security Official: Designated Privacy and Security Officer appointed

Workforce Security (§164.308(a)(3)):
  ✓ Authorization and Supervision: Role-based access documented
  ✓ Workforce Clearance: Background checks for staff with PHI access
  ✓ Termination Procedures: Access removal within 24 hours of termination

Information Access Management (§164.308(a)(4)):
  ✓ Isolating Health Care Clearinghouse Functions: N/A
  ✓ Access Authorization: Written access authorization procedures
  ✓ Access Establishment and Modification: Formal access request process

Security Awareness and Training (§164.308(a)(5)):
  ⚠ Training Program: Annual HIPAA training with 96% completion rate
  ⚠ Periodic Security Updates: Quarterly security reminders sent
  ✓ Protection from Malicious Software: Endpoint protection deployed
  ✓ Log-in Monitoring: Failed login attempt monitoring active
  ✓ Password Management: Strong password requirements enforced

Security Incident Procedures (§164.308(a)(6)):
  ✓ Response and Reporting: Incident response plan in place and tested

Contingency Plan (§164.308(a)(7)):
  ✓ Data Backup Plan: Daily automated PHI backups
  ✓ Disaster Recovery Plan: Documented and tested annually
  ✓ Emergency Mode Operation Plan: Alternate site procedures documented
  ✓ Testing and Revision: Annual tabletop exercises conducted
  ✓ Applications and Data Criticality Analysis: Completed

Evaluation (§164.308(a)(8)):
  ✓ Annual evaluation of security posture conducted

Business Associate Contracts (§164.308(b)(1)):
  ✓ Written Contract or Agreement: BAAs in place for all vendors
  ✓ Regular BA security assessments conducted

----------------------------------------------------------------------
3. PHYSICAL SAFEGUARDS (§164.310)
----------------------------------------------------------------------

${hipaaData.safeguards.filter(s => s.type === 'Physical').map(safeguard => `
${safeguard.requirement}: ${safeguard.title}
  Requirement Type: ${safeguard.requirement.includes('(a)') || safeguard.requirement.includes('(d)') ? 'REQUIRED' : 'ADDRESSABLE'}
  Implementation Status: ${safeguard.status}
  
  Assessment Findings: ${safeguard.notes}
  
  ${safeguard.status === 'Compliant' 
    ? '✓ COMPLIANT: Physical safeguards properly implemented and maintained.'
    : '⚠ PARTIALLY COMPLIANT: Minor physical security enhancements recommended.'}
`).join('\n')}

ADDITIONAL PHYSICAL SAFEGUARD FINDINGS:

Facility Access Controls (§164.310(a)(1)):
  ✓ Contingency Operations: Generator and UPS systems operational
  ✓ Facility Security Plan: Physical security plan documented
  ✓ Access Control and Validation: Badge access system deployed
  ✓ Maintenance Records: Facility maintenance logs maintained

Workstation Use (§164.310(b)):
  ✓ Workstation policies specify proper use of PHI systems

Workstation Security (§164.310(c)):
  ✓ Privacy screens and automatic screen locks implemented

Device and Media Controls (§164.310(d)(1)):
  ✓ Disposal: Secure media destruction procedures (NIST 800-88)
  ✓ Media Re-use: Sanitization procedures before device reuse
  ✓ Accountability: Asset inventory and tracking system
  ✓ Data Backup and Storage: Encrypted backups stored securely

----------------------------------------------------------------------
4. TECHNICAL SAFEGUARDS (§164.312)
----------------------------------------------------------------------

${hipaaData.safeguards.filter(s => s.type === 'Technical').map(safeguard => `
${safeguard.requirement}: ${safeguard.title}
  Requirement Type: REQUIRED
  Implementation Status: ${safeguard.status}
  
  Assessment Findings: ${safeguard.notes}
  
  ${safeguard.status === 'Compliant' 
    ? '✓ COMPLIANT: Technical controls effectively protect ePHI.'
    : '⚠ PARTIALLY COMPLIANT: Technical control enhancements needed. See remediation section.'}
`).join('\n')}

ADDITIONAL TECHNICAL SAFEGUARD FINDINGS:

Access Control (§164.312(a)(1)):
  ✓ Unique User Identification: Unique user IDs for all personnel
  ✓ Emergency Access Procedure: Break-glass procedures documented
  ⚠ Automatic Logoff: 15-minute idle timeout implemented
  ✓ Encryption and Decryption: AES-256 encryption for ePHI at rest

Audit Controls (§164.312(b)):
  ⚠ Audit logging implemented for all ePHI access
  ⚠ Log review procedures need enhanced documentation

Integrity (§164.312(c)(1)):
  ✓ Mechanism to Authenticate ePHI: Digital signatures and checksums
  ✓ ePHI integrity validation procedures in place

Person or Entity Authentication (§164.312(d)):
  ✓ Multi-factor authentication for remote access to ePHI systems
  ✓ Password complexity requirements enforced (12+ characters)

Transmission Security (§164.312(e)(1)):
  ✓ Integrity Controls: TLS 1.3 for all ePHI transmissions
  ✓ Encryption: End-to-end encryption for ePHI in transit
  ✓ VPN required for remote access to ePHI systems

----------------------------------------------------------------------
5. PROTECTED HEALTH INFORMATION (PHI) DATA FLOWS
----------------------------------------------------------------------

PHI DATA FLOW MAPPING: Complete

The following ePHI data flows have been identified and secured:

  1. Patient Registration → EHR System
     Security: TLS 1.3 encryption, MFA authentication

  2. EHR System → Billing System  
     Security: Internal encrypted channels, VLAN segmentation

  3. EHR System → External Labs (Business Associate)
     Security: sFTP with encryption, BAA in place

  4. Patient Portal → EHR System
     Security: HTTPS, certificate pinning, MFA

  5. Mobile Devices → EHR System
     Security: Mobile Device Management (MDM), containerization

  6. Backup Systems → Off-site Storage
     Security: AES-256 encrypted backups, secure transport

All ePHI transmissions utilize encryption meeting or exceeding current
NIST cryptographic standards.

----------------------------------------------------------------------
6. BUSINESS ASSOCIATE AGREEMENTS (BAAs)
----------------------------------------------------------------------

BUSINESS ASSOCIATE PROGRAM: Compliant

The covered entity maintains written Business Associate Agreements with
all third-party service providers that create, receive, maintain, or
transmit ePHI on behalf of the covered entity.

CURRENT BUSINESS ASSOCIATES: 8
BAAS ON FILE: 8 (100%)
LAST BA SECURITY ASSESSMENT: ${new Date(new Date().setMonth(new Date().getMonth() - 6)).toLocaleDateString()}

Business Associate Oversight:
  ✓ Annual security questionnaires sent to all BAs
  ✓ BA breach notification procedures documented
  ✓ Regular BA security posture reviews
  ✓ BA audit rights exercised annually

----------------------------------------------------------------------
7. RISK ANALYSIS & RISK MANAGEMENT
----------------------------------------------------------------------

LAST COMPREHENSIVE RISK ANALYSIS: ${new Date(new Date().setMonth(new Date().getMonth() - 4)).toLocaleDateString()}
NEXT SCHEDULED RISK ANALYSIS: ${new Date(new Date().setMonth(new Date().getMonth() + 8)).toLocaleDateString()}

RISK ANALYSIS METHODOLOGY: NIST SP 800-30 Rev 1

Risks Identified: 12
  ├─ High Risk: 0
  ├─ Medium Risk: 2 (under remediation)
  └─ Low Risk: 10 (accepted or mitigated)

Current Risk Mitigation Activities:
  • Enhanced audit log review procedures (Medium Risk)
  • Security awareness training frequency increase (Medium Risk)

----------------------------------------------------------------------
8. BREACH NOTIFICATION COMPLIANCE
----------------------------------------------------------------------

BREACH NOTIFICATION PROCEDURES: Documented and Tested

Per 45 CFR §§ 164.400-164.414 (Breach Notification Rule):
  ✓ Breach assessment procedures documented
  ✓ Breach notification templates prepared
  ✓ Breach response team identified and trained
  ✓ HHS breach portal access credentials maintained

REPORTABLE BREACHES (CURRENT REPORTING PERIOD): 0

NON-REPORTABLE INCIDENTS: 2
  • Lost unencrypted laptop (device encrypted, no breach)
  • Misdirected fax (retrieved before viewing, documented)

----------------------------------------------------------------------
9. REMEDIATION PLAN & TIMELINE
----------------------------------------------------------------------

IDENTIFIED DEFICIENCIES: ${partial}

Deficiency 1: Security Awareness Training Frequency
  Current State: Annual training with quarterly reminders
  Required Action: Increase training touchpoints to monthly
  Timeline: 60 days
  Responsible Party: Security Officer
  Resources: Learning management system configuration

Deficiency 2: Audit Log Review Procedures
  Current State: Logs captured but reviews not consistently documented
  Required Action: Formalize log review schedule and documentation
  Timeline: 30 days
  Responsible Party: IT Security Team
  Resources: SIEM tool implementation (in progress)

----------------------------------------------------------------------
10. HHS OCR AUDIT READINESS
----------------------------------------------------------------------

OCR AUDIT PROTOCOL READINESS: High

The organization maintains audit readiness through:
  ✓ Centralized HIPAA documentation repository
  ✓ Regular mock OCR audits (last conducted: ${new Date(new Date().setMonth(new Date().getMonth() - 7)).toLocaleDateString()})
  ✓ Designated OCR liaison (Privacy & Security Officer)
  ✓ 48-hour document production capability
  ✓ Staff trained on OCR interaction procedures

----------------------------------------------------------------------
11. RECOMMENDATIONS FOR CONTINUED COMPLIANCE
----------------------------------------------------------------------

1. SHORT-TERM (0-90 DAYS):
   • Complete remediation of identified deficiencies
   • Conduct HIPAA refresher training for workforce
   • Update risk analysis for any system changes
   • Review and update Business Associate Agreements

2. LONG-TERM (91-365 DAYS):
   • Implement advanced threat detection capabilities
   • Enhance encryption key management procedures
   • Develop comprehensive data loss prevention (DLP) program
   • Conduct penetration testing of systems containing ePHI

3. ONGOING:
   • Quarterly security awareness communications
   • Monthly security metrics reporting
   • Annual comprehensive HIPAA compliance assessment
   • Continuous monitoring of OCR guidance and enforcement actions

----------------------------------------------------------------------
12. COMPLIANCE REFERENCES
----------------------------------------------------------------------

• 45 CFR Part 160 - General Administrative Requirements
• 45 CFR §§ 164.302-164.318 - Security Standards (Security Rule)
• 45 CFR §§ 164.400-164.414 - Notification in the Case of Breach
• HHS Office for Civil Rights Audit Protocol
• NIST SP 800-66 Rev 1: Implementing the HIPAA Security Rule
• NIST SP 800-53 Rev 5: Security and Privacy Controls
• NIST Cybersecurity Framework: Healthcare Sector Profile

----------------------------------------------------------------------
ASSESSMENT TEAM & ATTESTATION
----------------------------------------------------------------------

Assessment Conducted By: S2AI Security Automation System
Assessment Methodology: Documentation review, technical testing, 
  interviews with key personnel, NIST 800-66 Rev 1 guidance

Privacy & Security Officer: _________________________ Date: __________
Chief Information Officer: _________________________ Date: __________
Compliance Officer: _________________________ Date: __________

DOCUMENT CLASSIFICATION: CONFIDENTIAL - ATTORNEY WORK PRODUCT
DISTRIBUTION: Privacy Officer, Security Officer, CIO, Legal Counsel
`;
        break;
        
      case 'soc2':
        const soc2Data = auditData as typeof SAMPLE_AUDIT_DATA.soc2;
        const noExceptions = soc2Data.criteria.filter(c => c.assessment === 'No Exceptions').length;
        const exceptions = soc2Data.criteria.filter(c => c.assessment === 'Exception Noted').length;
        
        specificContent = `
═══════════════════════════════════════════════════════════════════════
SOC 2 TYPE 2 READINESS ASSESSMENT REPORT
AICPA TRUST SERVICES CRITERIA
═══════════════════════════════════════════════════════════════════════

SERVICE ORGANIZATION: S2AI Network Infrastructure Services
REPORT TYPE: SOC 2 Type 2 Readiness Assessment (Pre-Audit)
TRUST SERVICES CATEGORIES: Security, Availability, Confidentiality
ASSESSMENT PERIOD: ${currentDate}
FRAMEWORK: AICPA Trust Services Criteria 2017
READINESS LEVEL: ${data.score >= 90 ? 'HIGH - AUDIT READY' : data.score >= 80 ? 'MODERATE - REMEDIATION NEEDED' : 'LOW - SIGNIFICANT WORK REQUIRED'}

CRITERIA ASSESSMENT SUMMARY:
  ├─ No Exceptions: ${noExceptions} criteria (${Math.round(noExceptions/soc2Data.criteria.length * 100)}%)
  └─ Exceptions Noted: ${exceptions} criteria (${Math.round(exceptions/soc2Data.criteria.length * 100)}%)

----------------------------------------------------------------------
1. COMMON CRITERIA ASSESSMENT
----------------------------------------------------------------------

${soc2Data.criteria.filter(c => c.category === 'Common Criteria').map(criterion => `
${criterion.id}: ${criterion.title}
  Assessment: ${criterion.assessment}
  Notes: ${criterion.notes}
  ${criterion.assessment === 'No Exceptions' ? '✓ Control effective' : '⚠ Exception noted'}
`).join('\n')}

----------------------------------------------------------------------
2. FINDINGS & RECOMMENDATIONS
----------------------------------------------------------------------

${soc2Data.findings.map((finding, idx) => `${idx + 1}. ${finding}`).join('\n\n')}

REFERENCES:
- AICPA Trust Services Criteria 2017
- AICPA SOC 2 Reporting Guide
`;
        break;
        
      default: // GAO
        const gaoData = auditData as typeof SAMPLE_AUDIT_DATA.gao;
        
        specificContent = `
═══════════════════════════════════════════════════════════════════════
GENERAL NETWORK SECURITY AUDIT REPORT
GAO FEDERAL INFORMATION SYSTEM CONTROLS AUDIT MANUAL (FISCAM)
═══════════════════════════════════════════════════════════════════════

AUDIT ENTITY: S2AI Network Infrastructure
AUDIT FRAMEWORK: GAO FISCAM / NIST Guidelines
AUDIT DATE: ${currentDate}
OVERALL SECURITY RATING: ${data.score >= 90 ? 'EFFECTIVE' : data.score >= 80 ? 'MODERATE' : 'NEEDS IMPROVEMENT'}

----------------------------------------------------------------------
1. CONTROL AREA ASSESSMENT
----------------------------------------------------------------------

${gaoData.controlAreas.map(area => `
${area.area}
  Rating: ${area.rating}
  Findings: ${area.findings}
  ${area.rating === 'Effective' ? '✓ Controls operating effectively' : '⚠ Improvement needed'}
`).join('\n')}

----------------------------------------------------------------------
2. KEY FINDINGS
----------------------------------------------------------------------

${gaoData.findings.map((finding, idx) => `${idx + 1}. ${finding}`).join('\n\n')}

REFERENCES:
- GAO FISCAM
- NIST SP 800-53 Rev. 5
- ISO/IEC 27001:2013
`;
    }
    
    // Build the full report with common elements and specific content
    return `
SECURITY AUDIT REPORT
=====================

REPORT DATE: ${currentDate}
AUDIT TYPE: ${auditTypeName}
COMPLIANCE SCORE: ${data.score}%
GENERATED BY: S2AI SECURITY AUTOMATION SYSTEM
CLASSIFICATION: CONFIDENTIAL

----------------------------------------------------------------------

EXECUTIVE SUMMARY
----------------

This security audit was conducted to assess compliance with ${auditTypeName} standards and 
regulations. The assessment evaluated network infrastructure, configurations, access 
controls, and security policies.

Overall security posture is rated at ${data.score}% compliance with applicable 
standards. ${data.score > 90 ? 'This indicates a strong security posture with minor improvements needed.' : 
data.score > 80 ? 'This indicates a good security posture with several improvements recommended.' : 
'This indicates significant security concerns that should be addressed promptly.'}

----------------------------------------------------------------------

${specificContent}

----------------------------------------------------------------------

KEY FINDINGS
-----------

${data.findings.map((finding: string, index: number) => `${index + 1}. ${finding}`).join('\n\n')}

----------------------------------------------------------------------

RECOMMENDATIONS
-------------

${data.recommendations.map((rec: string, index: number) => `${index + 1}. ${rec}`).join('\n\n')}

----------------------------------------------------------------------

AUDIT METHODOLOGY
----------------

Our security audit followed industry-standard practices for evaluating:
1. Network infrastructure security
2. Access control mechanisms
3. Security policies and compliance
4. Encryption implementation
5. Vulnerability management

The following steps were completed during this assessment:
${steps.map((step) => `- ${step.title}: ${step.description}`).join('\n')}

----------------------------------------------------------------------

CONCLUSION
---------

Based on our findings, the current security implementation requires 
${data.score > 90 ? 'minor adjustments' : data.score > 80 ? 'several improvements' : 'significant enhancements'} 
to meet all requirements of the ${auditTypeName} framework.

We recommend implementing the suggested security controls and conducting a follow-up 
audit within the next 90 days to verify remediation efforts.

----------------------------------------------------------------------

This report was generated by the S2AI Security Automation System
© ${new Date().getFullYear()} S2AI Secure Networks
Document ID: SEC-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}-${new Date().toISOString().split('T')[0]}
`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5 text-primary" />
            <span className="mr-2">Security Audit</span>
            {overallProgress === 100 || auditCompleted ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : auditInProgress ? (
              <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
            ) : null}
          </DialogTitle>
          <DialogDescription>
            S2AI can conduct a comprehensive security audit of your network infrastructure
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Audit Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Audit Framework:</label>
            <Select 
              value={selectedAuditType}
              onValueChange={onAuditTypeChange}
              disabled={auditInProgress}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an audit framework" />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id} className="text-left">
                    <div className="flex flex-col items-start">
                      <span>{type.name}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Start Audit Button */}
          {!auditInProgress && overallProgress === 0 && !auditCompleted && (
            <Button 
              className="w-full"
              onClick={onStartAudit}
            >
              Start Audit
            </Button>
          )}
          
          {/* Audit Progress and Steps */}
          {(auditInProgress || overallProgress > 0 || auditCompleted) && (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Audit Progress</span>
                  <span>{overallProgress}%</span>
                </div>
                <Progress 
                  value={overallProgress} 
                  className="h-2"
                />
              </div>
              
              <div className="space-y-4">
                {steps.map((step) => (
                  <div key={step.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {step.status === 'completed' || (auditCompleted && overallProgress === 100) ? (
                          <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                        ) : step.status === 'inProgress' ? (
                          <Loader2 className="h-4 w-4 mr-2 text-blue-500 animate-spin" />
                        ) : (
                          <div className="h-4 w-4 mr-2 rounded-full border border-muted-foreground" />
                        )}
                        <h4 className="text-sm font-medium">{step.title}</h4>
                      </div>
                      <span className="text-xs text-muted-foreground">{step.progress}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">{step.description}</p>
                    <div className="pl-6 pr-1">
                      <Progress value={step.progress} className={cn(
                        "h-1.5",
                        step.status === 'pending' ? "bg-muted/50" : ""
                      )} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
              
          {/* Report Preview Toggle */}
          {overallProgress >= 25 && (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => setReportPreviewVisible(!reportPreviewVisible)}
            >
              {reportPreviewVisible ? "Hide Report Preview" : "Show Report Preview"}
            </Button>
          )}
          
          {/* Report Preview */}
          {reportPreviewVisible && getReportPreviewContent()}
          
          {/* Download Report Button - Updated for better visibility and UX */}
          {(overallProgress === 100 || auditCompleted) && (
            <div className="pt-4">
              <Button 
                className="w-full flex items-center justify-center" 
                onClick={handleDownloadReport}
                variant={isGeneratingAiContent ? "outline" : "default"}
                disabled={isGeneratingAiContent}
              >
                {isGeneratingAiContent ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating AI report...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download AI-Enhanced Audit Report (.docx)
                  </>
                )}
              </Button>
              {aiGeneratedContent && (
                <p className="mt-2 text-xs text-center text-muted-foreground">
                  AI-enhanced report is ready for download as Word document
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SecurityAuditPopup;
